import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Download, Building, Smartphone } from 'lucide-react';

export default function Login({ onLogin }) {
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'customer'
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') toast.success('App installed successfully!');
      setDeferredPrompt(null);
      setShowInstallButton(false);
    }
  };

  const handleOtpSuccess = useCallback(async (data) => {
    setOtpLoading(true);
    try {
      const otpToken = data?.message || data?.token || data;
      if (!otpToken) {
        toast.error('OTP verification failed - no token received');
        return;
      }
      const response = await api.post('/auth/login-otp', { token: otpToken });
      const { token, user } = response.data;
      onLogin(user, token);
      toast.success('OTP Login successful! Welcome back!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'OTP login failed';
      toast.error(detail);
    } finally {
      setOtpLoading(false);
    }
  }, [onLogin]);

  const launchOtpWidget = useCallback(() => {
    const configuration = {
      widgetId: "366370744570363433393636",
      tokenAuth: "500195TcxpFAkMgn69b868adP1",
      success: (data) => {
        console.log('MSG91 success:', data);
        handleOtpSuccess(data);
      },
      failure: (error) => {
        console.log('MSG91 failure:', error);
        toast.error('OTP verification failed. Please try again.');
      },
    };

    if (typeof window.initSendOTP === 'function') {
      window.initSendOTP(configuration);
      return;
    }

    const urls = [
      'https://verify.msg91.com/otp-provider.js',
      'https://verify.phone91.com/otp-provider.js'
    ];
    let i = 0;
    function attempt() {
      const s = document.createElement('script');
      s.src = urls[i];
      s.async = true;
      s.onload = () => {
        if (typeof window.initSendOTP === 'function') {
          window.initSendOTP(configuration);
        }
      };
      s.onerror = () => {
        i++;
        if (i < urls.length) attempt();
        else toast.error('Failed to load OTP service. Please try password login.');
      };
      document.head.appendChild(s);
    }
    attempt();
  }, [handleOtpSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const payload = isRegister ? formData : { email: formData.email, password: formData.password };
      const response = await api.post(endpoint, payload);
      const { token, user } = response.data;
      onLogin(user, token);
      toast.success(isRegister ? 'Account created successfully!' : 'Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding (Desktop Only) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/90 via-emerald-800/85 to-slate-900/90" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-4">
            <div className="bg-white/95 rounded-2xl px-5 py-3 shadow-2xl">
              <img 
                src="https://ganesahomestate.com/images/logo.png" 
                alt="Ganesa Homestate" className="h-16 w-auto object-contain"
              />
            </div>
          </div>
          <div className="space-y-8">
            <div>
              <h2 className="text-5xl font-bold leading-tight">
                आपके सपनों का<br />
                <span className="text-emerald-400">घर</span> हमारी<br />
                जिम्मेदारी
              </h2>
              <p className="mt-6 text-lg text-emerald-100/80 max-w-md">
                गणेशा होमस्टेट - विश्वसनीय रियल एस्टेट पार्टनर। प्लॉट, फ्लैट और विला में निवेश के लिए सबसे भरोसेमंद नाम।
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'प्लॉट बुकिंग', desc: 'आसान किस्त योजना' },
                { label: 'पारदर्शी लेनदेन', desc: '100% कानूनी प्रॉपर्टी' },
                { label: 'ग्राहक पोर्टल', desc: 'अपनी बुकिंग ट्रैक करें' },
                { label: '24/7 सहायता', desc: 'हर कदम पर साथ' },
              ].map((feature, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
                  <p className="font-semibold">{feature.label}</p>
                  <p className="text-sm text-emerald-200">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-emerald-100 text-sm font-medium mb-2">"विश्वास की नींव पर बनता है आपका घर"</p>
            <p className="text-emerald-200/60 text-sm">© 2024 Ganesa Homestate. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 lg:justify-center">
        {/* Mobile Header */}
        <div className="lg:hidden relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800" />
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          </div>
          <div className="relative z-10 px-6 pt-8 pb-12 text-center text-white">
            <div className="inline-block bg-white/95 rounded-2xl px-5 py-3 shadow-2xl mb-4">
              <img 
                src="https://ganesahomestate.com/images/logo.png" 
                alt="Ganesa Homestate" className="h-14 w-auto object-contain mx-auto"
              />
            </div>
            <p className="text-emerald-200 text-sm mt-1">आपके सपनों का घर</p>
            {isMobile && showInstallButton && (
              <button
                onClick={handleInstallClick}
                data-testid="pwa-install-button"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium border border-white/30 hover:bg-white/30 transition-all"
              >
                <Download size={16} />
                Install App
              </button>
            )}
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-t-[2rem]" />
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-start lg:items-center justify-center px-6 py-6 lg:py-0">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-slate-100" data-testid="login-card">
              <div className="text-center mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800">
                  {isRegister ? 'Create Account' : 'Welcome Back'}
                </h2>
                <p className="text-slate-500 mt-1 text-sm">
                  {isRegister ? 'Start your journey with us' : 'Sign in to continue'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                {isRegister && (
                  <div className="space-y-1.5">
                    <Label className="text-slate-700 font-medium text-sm">Full Name</Label>
                    <div className="relative">
                      <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        data-testid="full-name-input"
                        placeholder="Enter your name"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        required autoComplete="off"
                        className="h-12 pl-12 rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-slate-700 font-medium text-sm">Email</Label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      type="email" data-testid="email-input" placeholder="name@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required autoComplete="off" name="login-email-field"
                      className="h-12 pl-12 rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-slate-700 font-medium text-sm">Password</Label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'} data-testid="password-input"
                      placeholder="--------"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required autoComplete="new-password" name="login-password-field"
                      className="h-12 pl-12 pr-12 rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                    <button
                      type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit" data-testid="submit-button" disabled={loading}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-semibold shadow-lg shadow-emerald-500/25 transition-all duration-300 group"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Please wait...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {isRegister ? 'Create Account' : 'Sign In'}
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </Button>
              </form>

              {/* OTP Login - Admin Only */}
              {!isRegister && (
                <div className="mt-4">
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-3 text-slate-400 uppercase tracking-wider">Admin</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    data-testid="otp-login-button"
                    onClick={launchOtpWidget}
                    disabled={otpLoading}
                    variant="outline"
                    className="w-full h-12 rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 font-semibold transition-all duration-300 group"
                  >
                    {otpLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin" />
                        Verifying OTP...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Smartphone size={18} />
                        Login with OTP (Admin)
                      </span>
                    )}
                  </Button>
                </div>
              )}

              <div className="mt-5 text-center">
                <p className="text-slate-500 text-sm">
                  {isRegister ? 'Already have an account?' : "Don't have an account?"}
                  <button
                    onClick={() => setIsRegister(!isRegister)}
                    className="ml-2 text-emerald-600 font-semibold hover:text-emerald-700 transition-colors"
                  >
                    {isRegister ? 'Sign In' : 'Sign Up'}
                  </button>
                </p>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-200">
                <button
                  onClick={() => navigate('/customer-login')}
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm text-slate-600 hover:text-emerald-600 transition-colors rounded-xl hover:bg-emerald-50"
                >
                  <Building size={18} />
                  <span>Customer? <strong>Login to view your bookings</strong></span>
                </button>
              </div>
            </div>

            <div className="lg:hidden text-center mt-6 pb-4">
              <p className="text-slate-400 text-xs">© 2024 Ganesa Homestate</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
